/* Availability-first collections, including page boundaries.
 * Shopify renders each card; two disjoint availability streams supply only
 * the slices needed for this page. The unfiltered response owns facet counts
 * and pagination. Explicit customer sorting and availability filters win.
 */
(() => {
  if (window.SICollectionSort) return;
  const availabilityKey = 'filter.v.availability';
  const cache = new Map();
  let initialController;

  function normalize(search) {
    const params = new URLSearchParams(search);
    if (!params.get('sort_by')) params.delete('sort_by');
    return params.toString();
  }

  function enabled(container, params) {
    return container?.hasAttribute('data-si-collection') &&
      container.dataset.siAvailableFirst !== 'false' && !params.get('sort_by');
  }

  async function fetchDocument(url, signal) {
    const key = url.toString();
    const saved = cache.get(key);
    if (saved && Date.now() - saved.at < 30000) {
      return new DOMParser().parseFromString(saved.html, 'text/html');
    }
    const requestController = new AbortController();
    const cancel = () => requestController.abort();
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    signal?.addEventListener('abort', cancel, { once: true });
    const timeout = setTimeout(cancel, 8000);
    let html;
    try {
      const response = await fetch(key, { signal: requestController.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
      html = await response.text();
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', cancel);
    }

    cache.set(key, { html, at: Date.now() });
    while (cache.size > 24) cache.delete(cache.keys().next().value);
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function cards(doc) {
    const grid = doc.querySelector('ul#product-grid');
    return grid ? Array.from(grid.children).filter((node) => node.matches('li[data-si-product-id]')) : [];
  }

  function sectionURL(params, section, page, availability) {
    const url = new URL(window.location.href);
    url.search = params.toString();
    url.hash = '';
    url.searchParams.set('section_id', section);
    url.searchParams.set('page', String(page));
    if (availability !== undefined) url.searchParams.set(availabilityKey, String(availability));
    return url;
  }

  async function sliceStream(params, section, pageSize, start, end, availability, signal) {
    if (start >= end) return [];
    const first = Math.floor(start / pageSize) + 1;
    const last = Math.floor((end - 1) / pageSize) + 1;
    const pages = [];
    for (let page = first; page <= last; page++) pages.push(page);
    const chunks = await Promise.all(pages.map(async (page) => {
      const doc = await fetchDocument(sectionURL(params, section, page, availability), signal);
      const items = cards(doc);
      const expected = availability === 1 ? 'true' : 'false';
      if (items.some((item) => item.dataset.siAvailable !== expected)) {
        throw new Error('Availability filter was not applied by Shopify');
      }
      return items;
    }));
    const items = chunks.flat().slice(start % pageSize, start % pageSize + end - start);
    if (items.length !== end - start) throw new Error('Incomplete collection page');
    return items;
  }

  // Collections without an enabled Shopify availability filter still work.
  // This slower fallback reads every page, at most three requests at a time.
  async function unfilteredSlice(doc, params, section, pageSize, total, currentPage, signal) {
    const pages = Math.ceil(total / pageSize);
    const chunks = new Array(pages);
    chunks[currentPage - 1] = cards(doc);
    let nextPage = 1;
    const worker = async () => {
      while (nextPage <= pages) {
        const page = nextPage++;
        if (page === currentPage) continue;
        chunks[page - 1] = cards(await fetchDocument(sectionURL(params, section, page), signal));
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, pages) }, worker));
    const all = chunks.flat();
    if (all.length !== total) throw new Error('Incomplete collection');
    const sorted = all.filter((item) => item.dataset.siAvailable === 'true')
      .concat(all.filter((item) => item.dataset.siAvailable === 'false'));
    return sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }

  async function prepare(html, search, signal) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const container = doc.getElementById('ProductGridContainer');
    const params = new URLSearchParams(normalize(search));
    if (!enabled(container, params)) return html;
    const chosenAvailability = params.getAll(availabilityKey).filter((value) => value === '0' || value === '1');
    if (new Set(chosenAvailability).size === 1) return html;
    const grid = container.querySelector('ul#product-grid');
    if (!grid) return html;
    const pageSize = Number(container.dataset.siPageSize);
    const currentPage = Number(container.dataset.siPage);
    const total = Number(container.dataset.siTotal);
    const available = Number(container.dataset.siAvailableCount);
    const sold = Number(container.dataset.siSoldCount);
    const section = grid.dataset.id;
    if (!pageSize || !currentPage || !Number.isFinite(total) || !section) return html;
    if (available === 0 || available === total) return html;
    // OR-selecting both availability checkboxes means all products.
    params.delete(availabilityKey);
    let items;
    if (available < 0 || !Number.isFinite(available) || available + sold !== total) {
      items = await unfilteredSlice(doc, params, section, pageSize, total, currentPage, signal);
    } else {
      const start = (currentPage - 1) * pageSize;
      const end = Math.min(start + pageSize, total);
      const [inStock, soldOut] = await Promise.all([
        sliceStream(params, section, pageSize, start, Math.min(end, available), 1, signal),
        sliceStream(params, section, pageSize, Math.max(0, start - available), end - available, 0, signal),
      ]);
      items = inStock.concat(soldOut);
    }
    const expectedCount = Math.min(pageSize, total - (currentPage - 1) * pageSize);
    if (items.length !== expectedCount || new Set(items.map((item) => item.dataset.siProductId)).size !== items.length) {
      throw new Error('Collection changed while sorting');
    }
    items.forEach((item, index) => {
      item.style.setProperty('--animation-order', index + 1);
      if (index < 2) item.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    });
    // Dawn emits shared card styles inside the first card. A slice can start
    // further down a filtered page, so retain those styles outside the list.
    const styles = new Map();
    const keepStyles = (parent) => parent.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
      styles.set(link.getAttribute('href'), link);
      link.remove();
    });
    keepStyles(grid);
    items.forEach(keepStyles);
    grid.before(...styles.values());
    grid.replaceChildren(...items);
    return doc.body.innerHTML;
  }

  function status(container, message) {
    let node = container.querySelector('[data-si-sort-status]');
    if (!node) {
      node = document.createElement('p');
      node.dataset.siSortStatus = '';
      node.className = 'si-sort-status';
      node.setAttribute('role', 'status');
      container.prepend(node);
    }
    node.textContent = message;
  }

  function cancelInitial() {
    const controller = initialController;
    initialController = undefined;
    controller?.abort();
    document.documentElement.classList.remove('si-collection-pending');
    if (controller) {
      const container = document.getElementById('ProductGridContainer');
      container?.classList.remove('si-sort-loading');
      container?.removeAttribute('aria-busy');
      container?.querySelector('[data-si-sort-status]')?.remove();
    }
  }

  async function initialize() {
    const container = document.getElementById('ProductGridContainer');
    const params = new URLSearchParams(window.location.search);
    if (!enabled(container, params)) return;
    cancelInitial();
    const controller = new AbortController();
    initialController = controller;
    const html = container.outerHTML;
    container.setAttribute('aria-busy', 'true');
    container.classList.add('si-sort-loading');
    status(container, 'Porządkujemy produkty…');
    try {
      const prepared = await prepare(html, params.toString(), controller.signal);
      if (controller.signal.aborted || !container.isConnected) return;
      if (prepared !== html) {
        const parsed = new DOMParser().parseFromString(prepared, 'text/html');
        container.innerHTML = parsed.getElementById('ProductGridContainer').innerHTML;
        container.querySelectorAll('.scroll-trigger').forEach((node) => node.classList.add('scroll-trigger--cancel'));
      }
      container.querySelector('[data-si-sort-status]')?.remove();
    } catch (error) {
      // The server-rendered catalog remains fully usable when enhancement fails.
      container.querySelector('[data-si-sort-status]')?.remove();
    } finally {
      if (initialController === controller) {
        container.classList.remove('si-sort-loading');
        container.removeAttribute('aria-busy');
        document.documentElement.classList.remove('si-collection-pending');
      }
    }
  }

  window.SICollectionSort = { prepare, normalize, cancelInitial };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
